/**
 * Config Studio — where a client's own manager changes how the agent behaves.
 *
 * Three things this screen refuses to do, and they are the whole design:
 *
 *  1. **It never shows a control that cannot take effect.** A field the runtime does not read is
 *     rendered disabled with "Saved, but Maya does not read this yet", and the registry behind that
 *     is derived by parsing the runtime rather than written down by hand.
 *  2. **It never saves one language of a per-language string.** Every declared language is edited
 *     together, and the Propose button says which language is missing rather than letting the
 *     manager ship English to Hindi customers.
 *  3. **It never makes a change effective by itself.** Everything here is a proposal with a reason;
 *     an owner or a manager approves it; the digest it becomes is shown, and so is the digest it
 *     can be rolled back to.
 *
 * The blast radius is on the screen before the decision: for a scoring change, Simulate re-scores
 * the last hundred leads and shows the tier movement; for everything else, each changed field says
 * what a change to it reaches — what the agent says, what this console shows, or what the replays
 * do. It used to print the source file and function that read the field, which is our repository
 * layout rather than a fact about the client's business. See PLANE_LABEL.
 *
 * Author: Danish Khan
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useMemo, useState, type ReactNode } from 'react';
import { Button } from '../components/Button.tsx';
import { Pill } from '../components/Pill.tsx';
import { ErrorState } from '../components/ErrorState.tsx';
import {
  editKey, missingLanguages, showValue, studioApi,
  type Edit, type InForce, type Knob, type KnobValue, type Proposal, type Simulation, type StudioClient,
  type Version,
} from './studioApi.ts';

/**
 * The console has no shared input class — every form styles its own the same way (People.tsx), so
 * these are the same declarations in one place rather than a new rule in the shared stylesheet.
 */
const INPUT = 'h-9 rounded-[var(--h1-radius)] border border-line-strong bg-bg px-3 text-sm text-fg disabled:opacity-50';
const AREA = 'rounded-[var(--h1-radius)] border border-line-strong bg-bg p-2 text-sm text-fg disabled:opacity-50';

const AREA_LABELS: Record<string, string> = {
  policies: 'Policies',
  funnel: 'Funnel',
  scoring: 'Scoring',
  prompts: 'Prompts',
  templates: 'Templates',
  routing: 'Routing',
  cadence: 'Cadence',
  hours: 'Hours',
};

/**
 * What a knob's reader means to the person changing it — the blast radius, not our source tree.
 *
 * The Studio derives `reader` by finding the line of our code that actually reads the field, which
 * is what makes "live" an evidenced claim rather than a label. Printing that line put strings like
 * `outbox/enqueue.ts:214 · readServiceWindow()` on the client's manager's screen: our file layout,
 * our function names, our line numbers, none of which are facts about their business and all of
 * which go stale the next time somebody moves a function. The half that IS theirs is which plane
 * the change lands in, and that is what these say.
 */
const PLANE_LABEL: Record<Knob['readers'][number]['plane'], string> = {
  runtime: 'changes what the agent says',
  console: 'changes what this console shows',
  lab: 'changes what the replays and the lab do',
};

type Draft = Record<string, Edit>;

export interface ConfigStudioProps {
  /** Injected in tests. The page is otherwise pointed at `/api/v1/studio`. */
  client?: StudioClient;
}

export function ConfigStudioPage({ client = studioApi }: ConfigStudioProps = {}): ReactNode {
  const qc = useQueryClient();
  const config = useQuery({ queryKey: ['studio', 'config'], queryFn: () => client.config() });
  const proposals = useQuery({ queryKey: ['studio', 'proposals'], queryFn: () => client.proposals() });
  const versions = useQuery({ queryKey: ['studio', 'versions'], queryFn: () => client.versions() });

  const [area, setArea] = useState('policies');
  const [draft, setDraft] = useState<Draft>({});
  const [reason, setReason] = useState('');
  const [failed, setFailed] = useState<string | null>(null);
  const [simulation, setSimulation] = useState<{ id: string; simulation: Simulation | null; detail: string } | null>(null);
  /**
   * What the last approval or rollback did to the RUNNING process, and which digest it was about.
   *
   * The digest is kept beside the report so the sentence lands on the version it is about rather
   * than on whichever row happens to be effective when it is read. This is session-scoped and a
   * reload loses it — which used to mean the honest state lasted exactly one page-load. It no
   * longer does: `GET /studio/config` now carries `inForce` for the effective version, computed
   * from the registry on every read, so a refresh keeps the truth. What this still covers is the
   * decision itself: it is the answer to the button the operator just pressed, it names a digest
   * the effective row may already have moved past (a rollback), and it is on the screen in the
   * moment that decides whether anybody restarts anything.
   */
  const [swap, setSwap] = useState<{ digest: string; report: InForce } | null>(null);

  const refresh = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['studio'] });
  };

  const propose = useMutation({
    mutationFn: (input: { reason: string; edits: Edit[] }) => client.propose(input),
    onSuccess: async () => { setDraft({}); setReason(''); setFailed(null); await refresh(); },
    onError: (e: Error) => setFailed(e.message),
  });
  const approve = useMutation({
    mutationFn: (id: string) => client.approve(id),
    onSuccess: async (r) => {
      setSwap({ digest: r.version.digest, report: r.effective });
      setFailed(null); setSimulation(null); await refresh();
    },
    onError: (e: Error) => setFailed(e.message),
  });
  const rejectMutation = useMutation({
    mutationFn: (v: { id: string; reason: string }) => client.reject(v.id, v.reason),
    onSuccess: async () => { setFailed(null); await refresh(); },
    onError: (e: Error) => setFailed(e.message),
  });
  const simulate = useMutation({
    mutationFn: (id: string) => client.simulate(id).then((r) => ({ id, ...r })),
    onSuccess: (r) => { setSimulation(r); setFailed(null); },
    onError: (e: Error) => setFailed(e.message),
  });
  const rollback = useMutation({
    mutationFn: (v: { digest: string; reason: string }) => client.rollback(v.digest, v.reason),
    onSuccess: async (r) => {
      setSwap({ digest: r.version.digest, report: r.effective });
      setFailed(null); await refresh();
    },
    onError: (e: Error) => setFailed(e.message),
  });

  const data = config.data;
  const knobs = useMemo(
    () => (data?.areas ?? []).flatMap((a) => a.knobs),
    [data],
  );
  const knobByField = useMemo(() => new Map(knobs.map((k) => [k.field, k] as const)), [knobs]);

  const edits = Object.values(draft);
  const incomplete = data
    ? edits.flatMap((e) => {
        const knob = knobByField.get(e.field);
        if (!knob?.localized) return [];
        const missing = missingLanguages(e.after, data.tenant.languages);
        return missing.length > 0 ? [{ label: knob.label, missing }] : [];
      })
    : [];

  if (config.isError) {
    return <ErrorState error={config.error} context="open the Config Studio" onRetry={() => void config.refetch()} />;
  }
  if (!data) return <p className="p-5 text-sm text-fg-muted">Reading what this tenant is configured to do…</p>;

  const setValue = (knob: Knob, value: KnobValue, after: unknown): void => {
    setDraft((d) => ({
      ...d,
      [editKey(value.file, value.pointer)]: { field: knob.field, file: value.file, pointer: value.pointer, before: value.value, after },
    }));
  };
  const discard = (file: string, pointer: string): void => {
    setDraft((d) => {
      const next = { ...d };
      delete next[editKey(file, pointer)];
      return next;
    });
  };

  const current = data.areas.find((a) => a.area === area)?.knobs ?? [];

  return (
    <div className="h1-scroll h-full overflow-y-auto px-5 py-5">
      <div className="mx-auto flex max-w-6xl flex-col gap-5">
        <header className="flex flex-wrap items-center gap-3">
          <h1 className="text-base font-semibold text-fg">Config Studio</h1>
          <Pill tone="brand" title={data.version.digest}>
            {data.version.kind === 'disk' ? 'shipped' : 'edited'} · {data.version.digest.slice(0, 12)}
          </Pill>
          <span className="text-xs text-fg-muted">
            {data.tenant.id} · {data.tenant.status} · answers in {data.tenant.languages.join(', ')}
          </span>
          {data.openProposals > 0 && <Pill tone="warn">{data.openProposals} awaiting a decision</Pill>}
        </header>

        {data.drift.drifted && (
          <div role="status" className="h1-card border-warn/40 bg-warn-soft/40 p-3 text-xs text-fg">
            <strong className="font-semibold">The pack on disk has changed since this version was approved.</strong>{' '}
            A change on disk to any other file reaches the agent on the next boot. These files are the Studio&apos;s and
            shadow the directory until a new proposal is approved over the new files:{' '}
            <span className="font-mono">{data.drift.shadowed.join(', ')}</span>
          </div>
        )}

        {/*
          The reload-proof half of the same truth. `data.inForce` is recomputed from the registry on
          every read, so this survives the refresh that used to reset the screen to reporting the
          database. Deliberately NOT an alert: nothing has just failed, and the operator is being
          told what the process is doing — the quiet-but-unmissable register the Analytics tiles use
          for "Unavailable" and the Health page for an "unknown" service.
        */}
        {!data.inForce.inForce && (
          <div role="status" data-testid="config-in-force" className="h1-card border-warn/40 bg-warn-soft/40 p-3 text-xs text-fg">
            <strong className="font-semibold">Not in force here. </strong>{data.inForce.detail}
          </div>
        )}

        {failed && (
          <div role="alert" className="h1-card border-danger/40 bg-danger-soft/40 p-3 text-xs text-fg">{failed}</div>
        )}

        <nav aria-label="Config areas" className="flex flex-wrap gap-1.5">
          {data.areas.map((a) => (
            <Button
              key={a.area}
              size="sm"
              variant={a.area === area ? 'primary' : 'secondary'}
              aria-pressed={a.area === area}
              onClick={() => setArea(a.area)}
            >
              {AREA_LABELS[a.area] ?? a.area} <span className="opacity-60">{a.knobs.length}</span>
            </Button>
          ))}
        </nav>

        <section className="flex flex-col gap-2.5">
          {current.map((knob) => (
            <KnobCard
              key={knob.field}
              knob={knob}
              languages={data.tenant.languages}
              draft={draft}
              editable={data.may.propose}
              onChange={setValue}
              onDiscard={discard}
            />
          ))}
        </section>

        {edits.length > 0 && (
          <section className="h1-card sticky bottom-3 flex flex-col gap-2.5 p-3" aria-label="Pending changes">
            <h2 className="text-sm font-semibold text-fg">{edits.length} change{edits.length === 1 ? '' : 's'} to propose</h2>
            <ul className="flex flex-col gap-1.5 text-xs">
              {edits.map((e) => {
                const knob = knobByField.get(e.field);
                return (
                  <li key={editKey(e.file, e.pointer)} className="flex flex-wrap items-baseline gap-2">
                    <span className="font-medium text-fg">{knob?.label ?? e.field}</span>
                    <span className="font-mono text-fg-muted">{e.pointer || e.file}</span>
                    <span className="text-fg-muted line-through">{showValue(e.before)}</span>
                    <span aria-hidden>→</span>
                    <span className="text-fg">{showValue(e.after)}</span>
                    {/* The blast radius, named per field: what this change reaches, or that
                        nothing reads it. See PLANE_LABEL for why it is not the source line. */}
                    <span className="text-fg-muted">
                      {knob?.reader ? PLANE_LABEL[knob.reader.plane] : knob?.note}
                    </span>
                  </li>
                );
              })}
            </ul>
            {incomplete.length > 0 && (
              <p role="alert" className="text-xs text-danger-soft-fg">
                {incomplete.map((i) => `${i.label} still needs ${i.missing.join(', ')}`).join('; ')}. This tenant answers
                in {data.tenant.languages.join(', ')}, and saving one language would send the others English.
              </p>
            )}
            <label className="flex flex-col gap-1 text-xs text-fg-muted">
              Why are you changing this?
              <input
                className={INPUT}
                value={reason}
                aria-label="Reason for this change"
                placeholder="600 characters cuts the delivery answer in half"
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            <div className="flex gap-2">
              <Button
                disabled={!data.may.propose || reason.trim().length === 0 || incomplete.length > 0 || propose.isPending}
                onClick={() => propose.mutate({ reason, edits })}
              >
                Propose {edits.length} change{edits.length === 1 ? '' : 's'}
              </Button>
              <Button variant="ghost" onClick={() => { setDraft({}); setReason(''); }}>Discard</Button>
            </div>
            {!data.may.propose && (
              <p className="text-xs text-fg-muted">
                Your role cannot propose a change to this tenant&apos;s configuration.
              </p>
            )}
          </section>
        )}

        <section aria-label="Proposals" className="flex flex-col gap-2.5">
          <h2 className="text-sm font-semibold text-fg">Proposals</h2>
          {(proposals.data ?? []).length === 0 && <p className="text-xs text-fg-muted">Nothing proposed yet.</p>}
          {(proposals.data ?? []).map((p) => (
            <ProposalCard
              key={p.id}
              proposal={p}
              mayApprove={data.may.approve}
              busy={approve.isPending || rejectMutation.isPending}
              simulation={simulation?.id === p.id ? simulation : null}
              onApprove={() => approve.mutate(p.id)}
              onReject={(why) => rejectMutation.mutate({ id: p.id, reason: why })}
              onSimulate={() => simulate.mutate(p.id)}
            />
          ))}
        </section>

        <VersionList
          versions={versions.data ?? []}
          mayRollback={data.may.approve}
          busy={rollback.isPending}
          swap={swap}
          effective={{ digest: data.version.digest, report: data.inForce }}
          onRollback={(digest, why) => rollback.mutate({ digest, reason: why })}
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ one knob */

function KnobCard({ knob, languages, draft, editable, onChange, onDiscard }: {
  knob: Knob;
  languages: string[];
  draft: Draft;
  editable: boolean;
  onChange: (knob: Knob, value: KnobValue, after: unknown) => void;
  onDiscard: (file: string, pointer: string) => void;
}): ReactNode {
  return (
    <article className={clsx('h1-card p-3', !knob.live && 'opacity-70')}>
      <header className="flex flex-wrap items-baseline gap-2">
        <h3 className="text-sm font-medium text-fg">{knob.label}</h3>
        <span className="font-mono text-[11px] text-fg-muted">{knob.field}</span>
        {knob.live ? (
          // "Live" is an evidenced claim — the Studio found the code that reads this field — but
          // the evidence is a line of OUR source and the client's manager is the one reading this
          // header. What they need is that the field is in force, and where a change lands.
          <Pill tone="ok" title="The runtime reads this field — the Studio checks the code that does">
            live · {PLANE_LABEL[knob.reader!.plane]}
          </Pill>
        ) : (
          <Pill tone="neutral">{knob.note}</Pill>
        )}
      </header>
      <p className="mt-0.5 text-xs text-fg-muted">{knob.help}</p>
      <div className="mt-2 flex flex-col gap-2">
        {knob.values.map((v) => {
          const key = editKey(v.file, v.pointer);
          const pending = draft[key];
          const value = pending ? pending.after : v.value;
          return (
            <div key={key} className="flex flex-col gap-1">
              {(v.instance.length > 0 || knob.values.length > 1) && (
                <span className="font-mono text-[11px] text-fg-muted">{v.instance.join(' · ') || v.pointer}</span>
              )}
              <ValueEditor
                knob={knob}
                languages={languages}
                value={value}
                disabled={!knob.live || !editable}
                label={`${knob.label}${v.instance.length > 0 ? ` (${v.instance.join(' ')})` : ''}`}
                onChange={(next) => onChange(knob, v, next)}
              />
              {pending && (
                <button
                  type="button"
                  className="self-start text-[11px] text-fg-muted underline"
                  onClick={() => onDiscard(v.file, v.pointer)}
                >
                  undo this change
                </button>
              )}
            </div>
          );
        })}
      </div>
    </article>
  );
}

function ValueEditor({ knob, languages, value, disabled, label, onChange }: {
  knob: Knob;
  languages: string[];
  value: unknown;
  disabled: boolean;
  label: string;
  onChange: (next: unknown) => void;
}): ReactNode {
  if (knob.localized) {
    const map = (value ?? {}) as Record<string, string>;
    return (
      <div className="flex flex-col gap-1">
        {languages.map((lang) => (
          <label key={lang} className="flex items-center gap-2 text-xs">
            <span className="w-16 shrink-0 font-mono text-fg-muted">{lang}</span>
            <input
              className={clsx(INPUT, "w-full")}
              aria-label={`${label} — ${lang}`}
              disabled={disabled}
              value={map[lang] ?? ''}
              onChange={(e) => onChange({ ...map, [lang]: e.target.value })}
            />
          </label>
        ))}
      </div>
    );
  }
  if (knob.kind === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          aria-label={label}
          disabled={disabled}
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
        />
        {value === true ? 'on' : 'off'}
      </label>
    );
  }
  if (knob.kind === 'enum') {
    return (
      <select
        className={INPUT} aria-label={label} disabled={disabled}
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
      >
        {(knob.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (knob.kind === 'number') {
    return (
      <input
        type="number" className={clsx(INPUT, "w-40")} aria-label={label} disabled={disabled}
        value={typeof value === 'number' ? value : ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      />
    );
  }
  if (knob.kind === 'markdown') {
    return (
      <textarea
        className={clsx(AREA, "h-48 font-mono text-xs")} aria-label={label} disabled={disabled}
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (knob.kind === 'list' || knob.kind === 'object') {
    // JSON, and the text is kept as typed until it parses: a controlled input that rejected every
    // keystroke that was not yet valid JSON could not be edited at all.
    return <JsonEditor label={label} disabled={disabled} value={value} onChange={onChange} />;
  }
  return (
    <input
      className={INPUT} aria-label={label} disabled={disabled}
      value={typeof value === 'string' ? value : ''}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function JsonEditor({ label, disabled, value, onChange }: {
  label: string; disabled: boolean; value: unknown; onChange: (next: unknown) => void;
}): ReactNode {
  const [text, setText] = useState(() => JSON.stringify(value, null, 1));
  const [bad, setBad] = useState<string | null>(null);
  return (
    <div className="flex flex-col gap-1">
      <textarea
        className={clsx(AREA, "h-24 font-mono text-xs")} aria-label={label} disabled={disabled}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          try { onChange(JSON.parse(e.target.value)); setBad(null); }
          catch (err) { setBad((err as Error).message); }
        }}
      />
      {bad && <span role="alert" className="text-[11px] text-danger-soft-fg">not valid JSON yet — {bad}</span>}
    </div>
  );
}

/* -------------------------------------------------------------- one proposal */

function ProposalCard({ proposal, mayApprove, busy, simulation, onApprove, onReject, onSimulate }: {
  proposal: Proposal;
  mayApprove: boolean;
  busy: boolean;
  simulation: { simulation: Simulation | null; detail: string } | null;
  onApprove: () => void;
  onReject: (reason: string) => void;
  onSimulate: () => void;
}): ReactNode {
  const [why, setWhy] = useState('');
  const open = proposal.status === 'open';
  return (
    <article className="h1-card flex flex-col gap-2 p-3">
      <header className="flex flex-wrap items-baseline gap-2">
        <Pill tone={proposal.status === 'approved' ? 'ok' : proposal.status === 'open' ? 'warn' : 'neutral'}>
          {proposal.status}
        </Pill>
        <span className="text-sm text-fg">{proposal.reason}</span>
        <span className="text-xs text-fg-muted">{proposal.author}</span>
        {proposal.digest && <span className="font-mono text-[11px] text-fg-muted">→ {proposal.digest.slice(0, 12)}</span>}
      </header>
      <ul className="flex flex-col gap-0.5 text-xs">
        {proposal.edits.map((e) => (
          <li key={editKey(e.file, e.pointer)} className="flex flex-wrap gap-2">
            <span className="font-mono text-fg-muted">{e.pointer || e.file}</span>
            <span className="text-fg-muted line-through">{showValue(e.before)}</span>
            <span aria-hidden>→</span>
            <span className="text-fg">{showValue(e.after)}</span>
          </li>
        ))}
      </ul>
      {!proposal.valid && (
        <div role="alert" className="rounded-[var(--h1-radius)] bg-danger-soft/40 p-2 text-xs">
          <p className="font-medium text-fg">This change does not make a pack the agent can run.</p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {proposal.validation.issues.map((i) => (
              <li key={`${i.field ?? ''}${i.message}`}>
                <span className="font-medium">{i.field ?? i.file ?? 'pack'}</span> — {i.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      {simulation && (
        <div className="rounded-[var(--h1-radius)] bg-info-soft/40 p-2 text-xs" aria-label="Simulation">
          <p className="font-medium text-fg">{simulation.detail}</p>
          {simulation.simulation && (
            <>
              <p className="text-fg-muted">{simulation.simulation.window}</p>
              <p className="mt-1">
                before — hot {simulation.simulation.before.hot}, warm {simulation.simulation.before.warm}, cold {simulation.simulation.before.cold}
              </p>
              <p>
                after — hot {simulation.simulation.after.hot}, warm {simulation.simulation.after.warm}, cold {simulation.simulation.after.cold}
              </p>
              <ul className="mt-1">
                {simulation.simulation.movements.map((m) => (
                  <li key={`${m.from}-${m.to}`}>{m.count} lead{m.count === 1 ? '' : 's'} {m.from} → {m.to}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
      {open && (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="secondary" onClick={onSimulate}>Simulate</Button>
          <Button size="sm" disabled={!mayApprove || !proposal.valid || busy} onClick={onApprove}>
            Approve
          </Button>
          <input
            className={clsx(INPUT, "h-7 w-56 text-xs")}
            aria-label={`Why reject: ${proposal.reason}`}
            placeholder="reason to reject"
            value={why}
            onChange={(e) => setWhy(e.target.value)}
          />
          <Button size="sm" variant="ghost" disabled={!mayApprove || why.trim().length === 0 || busy} onClick={() => onReject(why)}>
            Reject
          </Button>
          {!mayApprove && <span className="text-xs text-fg-muted">Approving is an owner&apos;s or a manager&apos;s decision.</span>}
          {!proposal.valid && <span className="text-xs text-fg-muted">An invalid proposal cannot be approved.</span>}
        </div>
      )}
      {proposal.status === 'rejected' && proposal.decisionReason && (
        <p className="text-xs text-fg-muted">Rejected by {proposal.decidedBy}: {proposal.decisionReason}</p>
      )}
    </article>
  );
}

/* --------------------------------------------------------------- the versions */

function VersionList({ versions, mayRollback, busy, swap, effective, onRollback }: {
  versions: Version[];
  mayRollback: boolean;
  busy: boolean;
  /** The last swap this session performed, or null when it has performed none. See the page. */
  swap: { digest: string; report: InForce } | null;
  /**
   * What `GET /studio/config` says about the EFFECTIVE version, and which digest that is.
   *
   * Recomputed by the API from the registry on every read, which is what makes this list honest on
   * a plain page-load. Without it a reload showed a green `effective` pill over a process that had
   * never picked the version up — the original defect, one refresh away from the fix for it.
   */
  effective: { digest: string; report: InForce };
  onRollback: (digest: string, reason: string) => void;
}): ReactNode {
  const [why, setWhy] = useState('');
  return (
    <section aria-label="Versions" className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold text-fg">Versions</h2>
      <p className="text-xs text-fg-muted">
        A digest is the version. Rolling back returns to the digest you name — nothing is deleted, so you can roll
        forward again.
      </p>
      {/*
        With no versions there is nothing to roll back TO, and the section still rendered its
        explanation and a "Why roll back?" box above an empty list — a control for an action that
        cannot be taken, which reads as a list that failed to load.
      */}
      {versions.length === 0 ? (
        <p className="text-xs text-fg-muted">
          No version has been recorded yet. The first one lands when a proposal is approved, or when this tenant’s
          pack is imported from disk.
        </p>
      ) : (
      <>
        <label className="flex flex-col gap-1 text-xs text-fg-muted">
          Why roll back?
          <input className={INPUT} aria-label="Reason for the rollback" value={why} onChange={(e) => setWhy(e.target.value)} />
        </label>
        <ul className="flex flex-col gap-1.5">
          {versions.map((v) => {
            // The report belongs to a digest, not to "the effective row": an approval and the
            // rollback that undoes it both land here, and a sentence about the wrong version is
            // worse than no sentence.
            //
            // The session's own swap wins where the two overlap, because it is the answer to the
            // button this operator pressed and it can speak about a digest the effective row has
            // already moved past. The read-side report covers everything else — above all the reload,
            // where there is no swap to remember and the row alone used to do the talking. It is
            // applied only to the row the API called effective, which is the only version it is a
            // statement about.
            const report = swap && swap.digest === v.digest ? swap.report
              : v.isEffective && effective.digest === v.digest ? effective.report
              : null;
            // Effective in the database AND running here. When the swap failed, this row is a
            // committed decision that the process has not picked up, and calling that "effective"
            // is the precise lie that let a ceiling change sit unapplied for fifteen minutes.
            const running = v.isEffective && (report === null || report.inForce);
            return (
              <li key={v.id} className="h1-card flex flex-wrap items-center gap-2 p-2.5 text-xs">
                <span className="font-mono text-fg">{v.digest.slice(0, 12)}</span>
                <Pill tone={v.kind === 'disk' ? 'neutral' : 'brand'}>{v.kind === 'disk' ? 'shipped' : 'edited'}</Pill>
                {v.isEffective && (
                  running
                    ? <Pill tone="ok">effective</Pill>
                    : <Pill tone="warn">approved · not running here</Pill>
                )}
                <span className="text-fg-muted">{v.notes ?? ''}</span>
                <span className="text-fg-muted">{v.approvedBy ?? ''}</span>
                {v.files.length > 0 && <span className="font-mono text-fg-muted">{v.files.join(', ')}</span>}
                {!v.isEffective && (
                  <Button
                    size="sm" variant="secondary" disabled={!mayRollback || why.trim().length === 0 || busy}
                    onClick={() => onRollback(v.digest, why)}
                  >
                    Roll back to {v.digest.slice(0, 12)}
                  </Button>
                )}
                {/*
                  The API's own sentence, in full, on the row — the same rule the Analytics tiles
                  follow for an unavailable number. Not a tooltip: somebody reading a screenshot of
                  this screen has to be able to see that the process did not take the change, or they
                  will fill the gap in themselves with "it says effective, so it is running".
                */}
                {report && (
                  <span
                    className={clsx('basis-full', report.inForce ? 'text-fg-muted' : 'text-fg')}
                    data-testid={`in-force-${v.digest.slice(0, 12)}`}
                  >
                    {report.inForce ? '' : <span className="font-semibold">Not in force here. </span>}
                    {report.detail}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </>
      )}
    </section>
  );
}
